import React from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';

import { T, FONT_STACK } from '../containers/customer/customers.theme';
import { CustomersKeyframes, PrimaryButton } from '../containers/customer/CustomersUI';
import { EmptyState } from '../containers/menu/MenuUI';
import DateRangePopover from '../containers/orders/DateRangePopover';
import ConfirmDialog from '../containers/admin/ConfirmDialog';
import { calendarLabel } from '../utils/appDate';

import StockList from '../containers/stock/StockList';
import MovementFeed from '../containers/stock/MovementFeed';
import StockEntryDialog from '../containers/stock/StockEntryDialog';
import StockItemDialog from '../containers/stock/StockItemDialog';
import StockHistoryDialog from '../containers/stock/StockHistoryDialog';
import CancelMovementDialog from '../containers/stock/CancelMovementDialog';
import { BoxIcon } from '../containers/stock/StockIcons';
import { COPY, READ_ROLES, WRITE_ROLES } from '../containers/stock/stock.copy';
import {
  setMovementDataFetched,
  setMovements,
  setStations,
  setStationDataFetched,
  setStockDataFetched,
  setStocks,
  stockChanged,
} from '../containers/stock/stockSlice';
import {
  cancelMovement,
  createMovement,
  createStock,
  deleteStock,
  errorMessage,
  fetchMovements,
  fetchStations,
  fetchStockHistory,
  fetchStocks,
  updateMovement,
  updateStock,
} from '../containers/stock/stockApi';
import { fetchVendors } from '../containers/vendor/vendorsApi';

/**
 * The store: what is on the shelf, and every document that moved it.
 *
 * Laid out like the customers and vendors books — a list that stays put on the
 * left, the thing that changes on the right — but pointed at documents rather
 * than at one item's ledger, because a supplier's invoice with ten items on it
 * is one document, and entering it ten times is the thing this screen exists
 * to avoid.
 *
 * ── What is deliberately NOT done here ──────────────────────────────────────
 *
 * No arithmetic. Quantity and average cost are moved by the server inside a
 * transaction, and after any write BOTH lists are simply marked stale and read
 * again. Patching them here would mean re-deriving a weighted average in the
 * browser, and a figure that disagrees with the phone by a cent is worse than
 * a second request.
 *
 * No delete of a movement, because there is no route for one: a posted
 * document is cancelled, reversed and kept.
 */

/** The period the feed opens on. The phone shows recent movements; this is that. */
const DEFAULT_RANGE = {
  start: moment().subtract(6, 'days').format('YYYY-MM-DD'),
  end: moment().format('YYYY-MM-DD'),
};

export default function Stock() {
  const dispatch = useDispatch();

  const activeUser = useSelector((state) => state.login.activeUser);
  const { business } = useSelector((state) => state.login.activeUser);
  const token = useSelector((state) => state.login.token);

  const {
    stocks,
    stockValue,
    isStockDataFetched,
    movements,
    movementSummary,
    isMovementDataFetched,
    stations,
    isStationDataFetched,
  } = useSelector((state) => state.stock);

  const canRead = READ_ROLES.indexOf(activeUser?.role) !== -1;
  const canWrite = WRITE_ROLES.indexOf(activeUser?.role) !== -1;
  const currency = business?.currencyMark || '$';

  /* ── what the screen is showing ───────────────────────────────────────── */

  const [tab, setTab] = React.useState('all');
  const [query, setQuery] = React.useState('');
  const [feedQuery, setFeedQuery] = React.useState('');
  const [filter, setFilter] = React.useState('all');
  const [stationFilter, setStationFilter] = React.useState('');
  const [range, setRange] = React.useState(DEFAULT_RANGE);
  const [datesOpen, setDatesOpen] = React.useState(false);
  const dateAnchor = React.useRef(null);

  const [loadingStocks, setLoadingStocks] = React.useState(false);
  const [loadingMovements, setLoadingMovements] = React.useState(false);
  const [listError, setListError] = React.useState('');
  const [notice, setNotice] = React.useState('');
  const [highlightId, setHighlightId] = React.useState(null);

  /* ── the dialogs ──────────────────────────────────────────────────────── */

  const [entry, setEntry] = React.useState(null); // { type } or { movement }
  const [itemForm, setItemForm] = React.useState(null); // { item } or { name }
  const [history, setHistory] = React.useState(null);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [canceling, setCanceling] = React.useState(null);
  const [removing, setRemoving] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  /**
   * Suppliers, for a purchase on credit and for a return.
   *
   * Kept in this screen rather than the vendors slice on purpose: that slice
   * is a book with balances that the Vendors screen refetches whenever one
   * moves, and this only needs names.
   *
   * A 403 here is EXPECTED and not an error: the vendor routes admit manager,
   * cashier, accountant and superadmin — not storekeeper — while the stock
   * routes admit storekeeper. So a storekeeper can post a credit purchase but
   * cannot list the suppliers to pick one. The screen says so plainly instead
   * of showing an empty dropdown with no explanation.
   */
  const [vendors, setVendors] = React.useState([]);
  const [vendorsBlocked, setVendorsBlocked] = React.useState(false);

  /* ── loading ──────────────────────────────────────────────────────────── */

  React.useEffect(() => {
    if (!canRead || isStockDataFetched || !business?._id) return undefined;

    let cancelled = false;
    setLoadingStocks(true);

    fetchStocks({ businessId: business._id, token })
      .then((payload) => {
        if (cancelled) return;
        dispatch(setStocks(payload));
        dispatch(setStockDataFetched(true));
      })
      .catch((error) => {
        if (!cancelled) setListError(errorMessage(error, 'Could not load the store'));
      })
      .finally(() => {
        if (!cancelled) setLoadingStocks(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canRead, isStockDataFetched, business, token, dispatch]);

  React.useEffect(() => {
    if (!canRead || !business?._id) return undefined;
    if (isMovementDataFetched) return undefined;

    let cancelled = false;
    setLoadingMovements(true);

    fetchMovements({
      businessId: business._id,
      token,
      startDate: range.start,
      endDate: range.end,
    })
      .then((payload) => {
        if (cancelled) return;
        dispatch(setMovements(payload));
        dispatch(setMovementDataFetched(true));
      })
      .catch((error) => {
        if (!cancelled) setListError(errorMessage(error, 'Could not load the movements'));
      })
      .finally(() => {
        if (!cancelled) setLoadingMovements(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canRead, business, token, range, isMovementDataFetched, dispatch]);

  React.useEffect(() => {
    if (!canRead || isStationDataFetched || !business?._id) return undefined;

    let cancelled = false;

    fetchStations({ businessId: business._id, token })
      .then((rows) => {
        if (cancelled) return;
        dispatch(setStations(rows));
        dispatch(setStationDataFetched(true));
      })
      // Silent: usage cannot be posted without a station, and the entry sheet
      // says so in its own words when the list is empty.
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [canRead, isStationDataFetched, business, token, dispatch]);

  React.useEffect(() => {
    if (!canWrite || !business?._id) return undefined;

    let cancelled = false;

    fetchVendors({ businessId: business._id, token })
      .then((rows) => {
        if (!cancelled) setVendors(rows);
      })
      .catch((error) => {
        if (cancelled) return;
        if (error?.response?.status === 403) setVendorsBlocked(true);
      });

    return () => {
      cancelled = true;
    };
  }, [canWrite, business, token]);

  /** Both lists after any write, because the server moved figures in both. */
  const refresh = React.useCallback(() => {
    dispatch(stockChanged());
  }, [dispatch]);

  /* ── writing ──────────────────────────────────────────────────────────── */

  const submitMovement = async (form) => {
    setSaving(true);

    try {
      const saved = form.id
        ? await updateMovement({
            id: form.id,
            reason: form.reason,
            station: form.station,
            vendor: form.vendor,
            pay: form.pay,
            note: form.note,
            date: form.date,
            lines: form.lines,
            token,
          })
        : await createMovement({
            businessId: business?._id,
            type: form.type,
            reason: form.reason,
            station: form.station,
            vendor: form.vendor,
            pay: form.pay,
            note: form.note,
            date: form.date,
            lines: form.lines,
            token,
          });

      setEntry(null);
      setHighlightId(saved?._id || null);
      setNotice(`${saved?.no || 'Movement'} ${form.id ? 'updated' : 'posted'}`);
      refresh();
    } catch (error) {
      setSaving(false);
      // Thrown back to the dialog, which shows the server's own sentence —
      // they are written for the person reading them.
      throw new Error(errorMessage(error, 'Could not post the movement'));
    }

    setSaving(false);
  };

  const confirmCancel = async (cancelReason) => {
    try {
      const canceled = await cancelMovement({ id: canceling._id, cancelReason, token });
      setNotice(`${canceled?.no || canceling.no} canceled. Quantities and accounting were reversed.`);
      refresh();
    } catch (error) {
      throw new Error(errorMessage(error, 'Could not cancel'));
    }
  };

  const submitItem = async (payload) => {
    try {
      if (itemForm?.item) {
        const saved = await updateStock({ id: itemForm.item._id, payload, token });
        // The history dialog may be open on this very item.
        setHistory((current) =>
          current && current.item._id === saved._id ? { ...current, item: saved } : current
        );
      } else {
        await createStock({ payload: { ...payload, business: business?._id }, token });
      }
      refresh();
    } catch (error) {
      throw new Error(errorMessage(error, 'Could not save the item'));
    }
  };

  const confirmRemove = async () => {
    const result = await deleteStock({ id: removing._id, token });
    // Delete or archive — the server decides which and its message says so.
    setNotice(result.message);
    closeHistory();
    refresh();
  };

  /* ── opening things ───────────────────────────────────────────────────── */

  /**
   * Which history request the screen is still interested in.
   *
   * Closing the dialog cannot cancel a request that is already in the air, so
   * the answer still arrives — and an answer that calls `setHistory` reopens a
   * dialog somebody has just dismissed. Every open takes a ticket; a reply
   * whose ticket is stale is dropped on the floor.
   *
   * It does the same job for a second item opened while the first is still
   * loading: the slower reply can no longer overwrite the newer one.
   */
  const historyTicket = React.useRef(0);

  /** The one way the history closes, so no route out of it forgets the ticket. */
  const closeHistory = React.useCallback(() => {
    historyTicket.current += 1;
    setHistory(null);
    setHistoryLoading(false);
  }, []);

  const openHistory = (item) => {
    const ticket = (historyTicket.current += 1);

    setHistory({ item, summary: null, history: [] });
    setHistoryLoading(true);

    fetchStockHistory({ id: item._id, token })
      .then((payload) => {
        if (historyTicket.current !== ticket) return;
        setHistory({
          item: payload.stock || item,
          summary: payload.summary,
          history: payload.history,
        });
      })
      .catch((error) => {
        if (historyTicket.current !== ticket) return;
        setHistory({ item, summary: null, history: [] });
        setNotice(errorMessage(error, 'Could not load the history'));
      })
      .finally(() => {
        if (historyTicket.current === ticket) setHistoryLoading(false);
      });
  };

  const openStockById = (stockId) => {
    const item = stocks.find((entry2) => entry2._id === stockId);
    if (item) openHistory(item);
  };

  const jumpToMovement = (movementId) => {
    closeHistory();
    setFilter('all');
    setFeedQuery('');
    setHighlightId(movementId);
  };

  const rangeLabel = React.useMemo(
    () => `${calendarLabel(range.start)} — ${calendarLabel(range.end)}`,
    [range]
  );

  /* ── the two states that are not the screen ───────────────────────────── */

  if (!canRead) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 90px)',
          background: T.canvas,
          fontFamily: FONT_STACK,
        }}
      >
        <span style={{ fontSize: 14, color: T.inkSoft }}>{COPY.notAllowed}</span>
      </div>
    );
  }

  if (isStockDataFetched && !stocks.length) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 90px)',
          background: T.canvas,
          fontFamily: FONT_STACK,
        }}
      >
        <CustomersKeyframes />
        <div style={{ maxWidth: 460 }}>
          <EmptyState
            icon={<BoxIcon />}
            title={COPY.nothingYet}
            action={
              canWrite ? (
                <React.Fragment>
                  <p
                    style={{
                      margin: '0 0 6px',
                      fontSize: 13,
                      color: T.inkSoft,
                      textAlign: 'center',
                      lineHeight: 1.5,
                    }}
                  >
                    {COPY.nothingYetBody}
                  </p>
                  <PrimaryButton onClick={() => setItemForm({})} style={{ padding: '0 24px' }}>
                    {COPY.addFirst}
                  </PrimaryButton>
                </React.Fragment>
              ) : null
            }
          />
        </div>

        {itemForm ? (
          <StockItemDialog
            item={itemForm.item}
            units={business?.stockUnits}
            onClose={() => setItemForm(null)}
            onSubmit={submitItem}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        height: 'calc(100vh - 90px)',
        background: T.canvas,
        color: T.ink,
        fontFamily: FONT_STACK,
        overflow: 'hidden',
      }}
    >
      <CustomersKeyframes />

      <StockList
        stocks={stocks}
        stockValue={stockValue}
        currency={currency}
        query={query}
        onQueryChange={setQuery}
        tab={tab}
        onTabChange={setTab}
        onOpen={openHistory}
        onAdd={() => setItemForm({})}
        canWrite={canWrite}
        loading={loadingStocks}
      />

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {listError || notice || vendorsBlocked ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              margin: '14px 18px 0',
              padding: '10px 14px',
              borderRadius: T.radiusControl,
              background: listError ? T.dangerTint : T.pendingTint,
              border: `1px solid ${listError ? T.dangerBorder : T.pendingBorder}`,
              fontSize: 13,
              color: listError ? T.danger : T.pendingInk,
            }}
          >
            <span>
              {listError ||
                notice ||
                'Your account cannot read the supplier list, so a purchase on credit or a return has to be posted by a manager.'}
            </span>
            {listError || notice ? (
              <button
                type="button"
                onClick={() => {
                  setListError('');
                  setNotice('');
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  font: 'inherit',
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: 'inherit',
                  cursor: 'pointer',
                }}
              >
                Dismiss
              </button>
            ) : null}
          </div>
        ) : null}

        <MovementFeed
          movements={movements}
          summary={movementSummary}
          stocks={stocks}
          currency={currency}
          loading={loadingMovements}
          query={feedQuery}
          onQueryChange={setFeedQuery}
          filter={filter}
          onFilterChange={(next) => {
            setFilter(next);
            // A station only means something on Out. Carried to another tab it
            // would keep hiding rows with nothing on screen explaining why.
            if (next !== 'out') setStationFilter('');
          }}
          stations={stations}
          stationFilter={stationFilter}
          onStationFilterChange={setStationFilter}
          rangeLabel={rangeLabel}
          rangeAnchorRef={dateAnchor}
          onPickRange={() => setDatesOpen((open) => !open)}
          rangePopover={
            datesOpen ? (
              <DateRangePopover
                range={range}
                anchorRef={dateAnchor}
                onClose={() => setDatesOpen(false)}
                onApply={(next) => {
                  setRange(next);
                  // A new period is a different question; the answer has to be
                  // read again.
                  dispatch(setMovementDataFetched(false));
                  setDatesOpen(false);
                }}
              />
            ) : null
          }
          onStockIn={() => setEntry({ type: 'in' })}
          onStockOut={() => setEntry({ type: 'out' })}
          onEdit={(movement) => setEntry({ movement })}
          onCancel={setCanceling}
          onOpenStock={openStockById}
          canWrite={canWrite}
          highlightId={highlightId}
        />
      </div>

      {entry ? (
        <StockEntryDialog
          type={entry.type}
          movement={entry.movement}
          stocks={stocks}
          stations={stations}
          vendors={vendors}
          currency={currency}
          busy={saving}
          onClose={() => setEntry(null)}
          onSubmit={submitMovement}
          onCreateStock={(name) => setItemForm({ name, fromEntry: entry })}
        />
      ) : null}

      {itemForm ? (
        <StockItemDialog
          item={itemForm.item}
          initialName={itemForm.name}
          units={business?.stockUnits}
          onClose={() => setItemForm(null)}
          onSubmit={submitItem}
        />
      ) : null}

      {history ? (
        <StockHistoryDialog
          item={history.item}
          summary={history.summary}
          history={history.history}
          loading={historyLoading}
          currency={currency}
          canWrite={canWrite}
          onClose={closeHistory}
          // Editing closes the history behind it. Two stacked dialogs left the
          // form sitting on top of the very numbers it is about to change,
          // with the item's name showing twice.
          onEdit={(item) => {
            closeHistory();
            setItemForm({ item });
          }}
          onDelete={(item) => {
            closeHistory();
            setRemoving(item);
          }}
          onOpenMovement={jumpToMovement}
        />
      ) : null}

      {canceling ? (
        <CancelMovementDialog
          movement={canceling}
          currency={currency}
          onClose={() => setCanceling(null)}
          onConfirm={confirmCancel}
        />
      ) : null}

      {removing ? (
        <ConfirmDialog
          title={COPY.deleteTitle(removing.name)}
          body={COPY.deleteBody}
          // The server archives an item that appears on any movement rather
          // than deleting it, and says so in the message this screen then
          // shows. It is not offered as a choice because it is not one.
          note="An item that appears on a movement is archived instead, and its history is kept."
          confirmLabel={COPY.delete}
          cancelLabel={COPY.cancel}
          onClose={() => setRemoving(null)}
          onConfirm={confirmRemove}
        />
      ) : null}
    </div>
  );
}
